import { ReactNode } from 'react'
import classNames from 'classnames'
import moment from 'jalali-moment'
import { faFull } from 'utilities/helper/format'

/** Small shared form/layout primitives for the cashbox manager screens. */

export function Field({
    label,
    hint,
    error,
    children,
}: {
    label: string
    hint?: string
    error?: string
    children: ReactNode
}) {
    return (
        <label className="block mb-4">
            <span className="block text-sm text-text-body mb-1.5">{label}</span>
            {children}
            {error ? (
                <span className="block text-xs text-[#B3261E] mt-1">
                    {error}
                </span>
            ) : hint ? (
                <span className="block text-xs text-text-secondary mt-1">
                    {hint}
                </span>
            ) : null}
        </label>
    )
}

export const inputClass =
    'w-full h-11 rounded-[10px] border border-divider bg-white px-3 text-sm outline-none focus:border-green-1 disabled:bg-[#F5F5F5] disabled:text-text-secondary'

export function TextInput(
    props: React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
) {
    const { invalid, className, ...rest } = props
    return (
        <input
            {...rest}
            className={classNames(
                inputClass,
                invalid && 'border-[#B3261E]',
                className
            )}
        />
    )
}

/**
 * Money input.
 *
 * Shows grouped fa-IR digits while holding a plain integer in state. Users
 * type Persian OR Latin digits; both are folded to Latin before parsing, so
 * «۵۰۰۰۰۰» and "500000" both mean 500000 rather than NaN.
 */
export function MoneyInput({
    value,
    onChange,
    disabled,
    invalid,
}: {
    value: number
    onChange: (value: number) => void
    disabled?: boolean
    invalid?: boolean
}) {
    return (
        <div className="relative">
            <input
                inputMode="numeric"
                disabled={disabled}
                value={value ? faFull.format(value) : ''}
                onChange={(event) => onChange(parseDigits(event.target.value))}
                className={classNames(
                    inputClass,
                    'pl-16',
                    invalid && 'border-[#B3261E]'
                )}
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-text-secondary">
                تومان
            </span>
        </div>
    )
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

export function toLatinDigits(input: string): string {
    return input.replace(/[۰-۹٠-٩]/g, (digit) => {
        const persian = PERSIAN_DIGITS.indexOf(digit)
        if (persian > -1) return String(persian)
        return String(ARABIC_DIGITS.indexOf(digit))
    })
}

export function parseDigits(input: string): number {
    const digits = toLatinDigits(input).replace(/[^0-9]/g, '')
    return digits ? parseInt(digits, 10) : 0
}

/**
 * Normalize a typed phone number to the `+989xxxxxxxxx` the backend demands.
 *
 * `is_phone_number_valid` matches `^\+989\d{9}$` and nothing else, so
 * `09121234567` — the form every Iranian actually types — is rejected with a
 * 401 whose message is in English. Convert here instead of teaching users a
 * format.
 */
export function normalizePhone(input: string): string {
    let value = toLatinDigits(input).replace(/[\s-()]/g, '')
    if (value.startsWith('00')) value = `+${value.slice(2)}`
    if (value.startsWith('0')) value = `+98${value.slice(1)}`
    else if (value.startsWith('9') && value.length === 10) value = `+98${value}`
    else if (value.startsWith('98')) value = `+${value}`
    return value
}

export const isValidPhone = (input: string) =>
    /^\+989\d{9}$/.test(normalizePhone(input))

export function PrimaryButton({
    children,
    className,
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button
            type="button"
            {...rest}
            className={classNames(
                'w-full h-12 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99] disabled:opacity-50 disabled:active:scale-100',
                className
            )}
        >
            {children}
        </button>
    )
}

export function SecondaryButton({
    children,
    className,
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button
            type="button"
            {...rest}
            className={classNames(
                'w-full h-12 rounded-[10px] border border-divider bg-white text-text-body text-sm active:scale-[0.99] disabled:opacity-50',
                className
            )}
        >
            {children}
        </button>
    )
}

export function DangerButton({
    children,
    className,
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <button
            type="button"
            {...rest}
            className={classNames(
                'w-full h-12 rounded-[10px] border border-[#F5C2BD] bg-white text-[#B3261E] text-sm active:scale-[0.99] disabled:opacity-50',
                className
            )}
        >
            {children}
        </button>
    )
}

export function Card({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) {
    return (
        <div
            className={classNames(
                'rounded-card border border-divider bg-white p-3',
                className
            )}
        >
            {children}
        </div>
    )
}

export function Notice({
    tone = 'info',
    children,
}: {
    tone?: 'info' | 'warn' | 'error'
    children: ReactNode
}) {
    const tones = {
        info: 'border-[#BBDEFB] bg-[#E8F4FD] text-[#0B5C8F]',
        warn: 'border-[#F0C36D] bg-[#FFF8E6] text-[#8A6100]',
        error: 'border-[#F5C2BD] bg-[#FDECEA] text-[#B3261E]',
    }
    return (
        <div className={classNames('rounded-card border p-3', tones[tone])}>
            <p className="text-xs leading-5">{children}</p>
        </div>
    )
}

/** Turns a DRF error body into something worth showing a person. */
export function apiMessage(error: any, fallback = 'مشکلی پیش آمده!'): string {
    const data = error?.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (typeof data.message === 'string') return data.message
    if (typeof data.detail === 'string') return data.detail
    if (typeof data.error === 'string') return data.error
    // serializer errors: {"field": ["msg"]}
    const first = Object.values(data)[0]
    if (Array.isArray(first) && typeof first[0] === 'string') return first[0]
    return fallback
}

/**
 * Valid days for a RECURRING monthly draw: 1–29 only.
 *
 * A Jalali month is 31 days (فروردین–شهریور), 30 days (مهر–بهمن) or 29/30
 * (اسفند, leap-dependent), so 30 and 31 do not exist every month — and the
 * backend does not cope. `Period.calculate_draw_date` patches exactly one
 * case (`next_month == 12 and next_day == 30 -> 29`) and otherwise hands the
 * day straight to `JalaliDate(year, month, day)`, which RAISES for day 31 in
 * months 7–12. A box set to the 31st therefore breaks the moment its next
 * draw lands in the second half of the year.
 *
 * 29 is the largest day present in every month of every year, so the picker
 * stops there rather than offering a value that works until اسفند.
 */
export const DAYS_OF_MONTH = Array.from({ length: 29 }, (_, i) => i + 1)

/**
 * Real day count of a Jalali month — for picking ONE specific date.
 *
 * Delegated to jalali-moment rather than a hand-rolled leap rule: the obvious
 * 2820-year arithmetic gets 1403 and 1404 exactly backwards.
 */
export const daysInJalaliMonth = (year: number, month: number): number =>
    moment().jYear(year).jMonth(month - 1).jDate(1).jDaysInMonth()

export const WEEK_DAYS = [
    { value: 6, label: 'شنبه' },
    { value: 7, label: 'یکشنبه' },
    { value: 1, label: 'دوشنبه' },
    { value: 2, label: 'سه‌شنبه' },
    { value: 3, label: 'چهارشنبه' },
    { value: 4, label: 'پنجشنبه' },
    { value: 5, label: 'جمعه' },
]
